import React, { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MontserratText } from "@/components/Typography";
import Image from "next/image";
import linkImage from "@/utils/linkImage";

interface StatItem {
  label: string;
  value: string;
}

interface StatsSectionProps {
  description: string;
  stats?: StatItem[];
  leftCloudImage?: string;
  rightCloudImage?: string;
  gateImage?: string;
}

// Reusable CountUp component with easeOutExpo easing and intersection observer
const CountUp = ({ value }: { value: string }) => {
  const [displayValue, setDisplayValue] = useState("");
  const elementRef = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const numRegex = /(\d+([,.]\d+)?)/;
    const match = value.match(numRegex);

    if (!match) {
      setDisplayValue(value);
      return;
    }

    const numericStr = match[0].replace(",", ".");
    const targetNum = parseFloat(numericStr);
    const startIndex = match.index || 0;
    const endIndex = startIndex + match[0].length;

    const prefix = value.substring(0, startIndex);
    const suffix = value.substring(endIndex);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTimestamp: number | null = null;
          const duration = 2000; // 2 seconds for a premium feel

          const step = (timestamp: number) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min(
              (timestamp - startTimestamp) / duration,
              1,
            );

            // Easing: easeOutExpo
            const easeProgress =
              progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            const currentNum = easeProgress * targetNum;

            let formattedNum = "";
            if (value.includes(",")) {
              formattedNum = currentNum.toFixed(1).replace(".", ",");
            } else if (value.includes(".")) {
              formattedNum = currentNum.toFixed(1);
            } else {
              formattedNum = Math.floor(currentNum).toString();
            }

            setDisplayValue(`${prefix}${formattedNum}${suffix}`);

            if (progress < 1) {
              window.requestAnimationFrame(step);
            } else {
              setDisplayValue(value);
            }
          };

          window.requestAnimationFrame(step);
        }
      },
      { threshold: 0.1 },
    );

    const currentRef = elementRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [value, hasAnimated]);

  return <span ref={elementRef}>{displayValue || value}</span>;
};

export const StatsSection = ({
  description,
  stats,
  gateImage,
}: StatsSectionProps) => {
  // =========================================================================
  // DỄ DÀNG CĂN CHỈNH VỊ TRÍ MÂY TẠI ĐÂY (Đơn vị %)
  // Thay đổi các con số dưới đây để dịch chuyển mây theo ý muốn của bạn
  // =========================================================================
  const leftCloudConfig = {
    topMobile: "16%", // Cách mép trên (Mobile)
    topTablet: "18%", // Cách mép trên (Tablet)
    topDesktop: "13%", // Cách mép trên (Desktop)
    leftMobile: "-6%", // Cách mép trái (Mobile)
    leftDesktop: "-2%", // Cách mép trái (Desktop)
    widthMobile: "42%", // Chiều rộng mây (Mobile)
    widthDesktop: "34%", // Chiều rộng mây (Desktop)
  };

  const rightCloudConfig = {
    topMobile: "18%", // Cách mép trên (Mobile)
    topTablet: "20%", // Cách mép trên (Tablet)
    topDesktop: "23%", // Cách mép trên (Desktop)
    rightMobile: "-6%", // Cách mép phải (Mobile)
    rightDesktop: "-2%", // Cách mép phải (Desktop)
    widthMobile: "42%", // Chiều rộng mây (Mobile)
    widthDesktop: "30%", // Chiều rộng mây (Desktop)
  };

  // Clouds are animated continuously in float state

  const [leftCloudLoaded, setLeftCloudLoaded] = useState(false);
  const [rightCloudLoaded, setRightCloudLoaded] = useState(false);
  const [gateLoaded, setGateLoaded] = useState(false);

  const leftCloudRefImg = useRef<HTMLImageElement>(null);
  const rightCloudRefImg = useRef<HTMLImageElement>(null);
  const gateRefImg = useRef<HTMLImageElement>(null);

  const descRef = useRef<HTMLDivElement>(null);
  const isDescInView = useInView(descRef, { once: true, amount: 0.1 });

  const statsRef = useRef<HTMLDivElement>(null);
  const isStatsInView = useInView(statsRef, { once: true, amount: 0.1 });

  const cloudsRef = useRef<HTMLDivElement>(null);

  const gateRef = useRef<HTMLDivElement>(null);
  const isGateInView = useInView(gateRef, { once: true, amount: 0.1 });

  useEffect(() => {
    if (leftCloudRefImg.current?.complete) setLeftCloudLoaded(true);
    if (rightCloudRefImg.current?.complete) setRightCloudLoaded(true);
    if (gateRefImg.current?.complete) setGateLoaded(true);
  }, []);

  // Custom Scrollbar synchronization hooks
  const scrollContainerRef = useRef<HTMLParagraphElement>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);
  const scrollbarThumbRef = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState("100%");
  const [thumbTop, setThumbTop] = useState("0px");
  const [showCustomScrollbar, setShowCustomScrollbar] = useState(false);

  const updateScrollbar = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const contentHeight = container.scrollHeight;
    const viewportHeight = container.clientHeight;
    const scrollTop = container.scrollTop;

    // Nếu nội dung ngắn hơn màn hình, ẩn thanh cuộn
    if (contentHeight <= viewportHeight) {
      setShowCustomScrollbar(false);
      return;
    } else {
      setShowCustomScrollbar(true);
    }

    const track = scrollbarTrackRef.current;
    if (!track) return;

    // Tính toán tỷ lệ phần trăm nội dung hiển thị
    const heightPercentage = viewportHeight / contentHeight;
    // Đảm bảo thumb không quá nhỏ (min 30px)
    const calculatedThumbHeight = Math.max(
      heightPercentage * track.clientHeight,
      30,
    );

    // Tính toán vị trí top của thumb
    const maxScrollTop = contentHeight - viewportHeight;
    const maxThumbTop = track.clientHeight - calculatedThumbHeight;

    const scrollPercentage = maxScrollTop > 0 ? scrollTop / maxScrollTop : 0;
    const calculatedThumbTop = scrollPercentage * maxThumbTop;

    setThumbHeight(`${calculatedThumbHeight}px`);
    setThumbTop(`${calculatedThumbTop}px`);
  };

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const container = scrollContainerRef.current;
    const thumb = scrollbarThumbRef.current;
    const track = scrollbarTrackRef.current;
    if (!container || !thumb || !track) return;

    const startY = e.clientY;
    const startScrollTop = container.scrollTop;
    const maxScrollTop = container.scrollHeight - container.clientHeight;
    const maxThumbTop = track.clientHeight - thumb.clientHeight;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const deltaScrollTop =
        maxThumbTop > 0 ? (deltaY / maxThumbTop) * maxScrollTop : 0;
      container.scrollTop = startScrollTop + deltaScrollTop;
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };

    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTrackMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    const thumb = scrollbarThumbRef.current;
    const track = scrollbarTrackRef.current;
    if (!container || !thumb || !track || e.target === thumb) return;

    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;

    const thumbHeightVal = thumb.clientHeight;
    const maxThumbTop = track.clientHeight - thumbHeightVal;

    let targetThumbTop = clickY - thumbHeightVal / 2;
    targetThumbTop = Math.max(0, Math.min(targetThumbTop, maxThumbTop));

    const maxScrollTop = container.scrollHeight - container.clientHeight;
    const scrollPercentage = maxThumbTop > 0 ? targetThumbTop / maxThumbTop : 0;
    container.scrollTop = scrollPercentage * maxScrollTop;
  };

  useEffect(() => {
    updateScrollbar();
    window.addEventListener("resize", updateScrollbar);
    return () => window.removeEventListener("resize", updateScrollbar);
  }, [description]);

  useEffect(() => {
    if (showCustomScrollbar) {
      const handle = requestAnimationFrame(updateScrollbar);
      return () => cancelAnimationFrame(handle);
    }
  }, [showCustomScrollbar]);

  const descVariants = {
    initial: {
      opacity: 0,
      y: 20,
      transition: { duration: 0.8, ease: "easeIn" as const },
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 1.2, delay: 0.2, ease: "easeOut" as const },
    },
  };

  const statItemVariants = {
    initial: {
      opacity: 0,
      y: 30,
      transition: { duration: 0.5, ease: "easeIn" as const },
    },
    animate: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1,
        ease: "easeOut" as const,
      },
    }),
  };

  const gateVariants = {
    initial: {
      opacity: 0,
      scale: 0.75,
      y: 30,
      transition: { duration: 0.8, ease: "easeIn" as const },
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 1.2, ease: "easeOut" as const },
    },
  };

  const leftCloudVariants = {
    float: {
      x: [0, 35, -35, 0],
      y: [0, 3, -3, 0],
      opacity: 1,
      transition: {
        repeat: Infinity,
        duration: 20,
        ease: "easeInOut" as const,
      },
    },
  };

  const rightCloudVariants = {
    float: {
      x: [0, -35, 35, 0],
      y: [0, -3, 3, 0],
      opacity: 1,
      transition: {
        repeat: Infinity,
        duration: 24,
        ease: "easeInOut" as const,
      },
    },
  };

  return (
    <section
      id="stats-section-container"
      className="relative bg-[#0d3f4d] pb-0 z-20 overflow-x-hidden"
    >
      {/* Nhúng mã CSS tùy biến để bạn dễ dàng thay đổi cấu hình mây responsive */}
      <style>{`
        .left-cloud {
          top: ${leftCloudConfig.topMobile};
          left: ${leftCloudConfig.leftMobile};
          width: ${leftCloudConfig.widthMobile};
        }
        .right-cloud {
          top: ${rightCloudConfig.topMobile};
          right: ${rightCloudConfig.rightMobile};
          width: ${rightCloudConfig.widthMobile};
        }

        @media (min-width: 640px) {
          .left-cloud {
            top: ${leftCloudConfig.topTablet};
          }
          .right-cloud {
            top: ${rightCloudConfig.topTablet};
          }
        }

        @media (min-width: 1024px) {
          .left-cloud {
            top: ${leftCloudConfig.topDesktop};
            left: ${leftCloudConfig.leftDesktop};
            width: ${leftCloudConfig.widthDesktop};
          }
          .right-cloud {
            top: ${rightCloudConfig.topDesktop};
            right: ${rightCloudConfig.rightDesktop};
            width: ${rightCloudConfig.widthDesktop};
          }
        }
      `}</style>

      <motion.div
        ref={descRef}
        variants={descVariants}
        initial="initial"
        animate={isDescInView ? "animate" : "initial"}
        className="w-full max-w-5xl lg:max-w-7xl mx-auto mb-12 sm:mb-16 mt-8 sm:mt-12 md:mt-16 lg:mt-20 relative z-30 px-2"
      >
        <div className="relative mx-auto pl-4">
          {/* Custom Scrollbar Container (on the left) */}
          {showCustomScrollbar && (
            <div
              className="absolute left-0 top-[10%] bottom-[10%] w-[4px] z-40 pointer-events-auto cursor-pointer"
              onMouseDown={handleTrackMouseDown}
              ref={scrollbarTrackRef}
            >
              {/* Track */}
              <div className="absolute inset-0 bg-white/20 rounded-[4px]" />
              {/* Thumb */}
              <div
                ref={scrollbarThumbRef}
                className="absolute left-0 w-full bg-[#e5d19d] hover:bg-[#f7e7b6] rounded-[4px] cursor-pointer transition-colors duration-200 ease-in-out"
                style={{
                  height: thumbHeight,
                  top: thumbTop,
                }}
                onMouseDown={handleThumbMouseDown}
              />
            </div>
          )}

          <MontserratText
            ref={scrollContainerRef}
            onScroll={updateScrollbar}
            className="leading-relaxed tracking-wider max-h-[16em] overflow-y-auto no-scrollbar whitespace-pre-line text-justify block w-full"
            style={{ textAlign: "justify" }}
          >
            {description}
          </MontserratText>
        </div>
      </motion.div>

      {/* Stats Wrapper to keep Stacking Context separate but aligned */}
      <div className="relative w-full lg:mt-40">
        {/* Statistics Grid (z-30: in front of the Gate Image) */}
        <div
          ref={statsRef}
          className="max-w-[70rem] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8 lg:gap-18 relative z-30 px-6"
        >
          {stats?.map((stat, index) => (
            <motion.div
              key={index}
              custom={index}
              variants={statItemVariants}
              initial="initial"
              animate={isStatsInView ? "animate" : "initial"}
              className="flex flex-col items-center text-center relative"
            >
              {/* Statistic Label Wrapper (Fixed height container so values and dots align horizontally) */}
              <div className="w-full h-20 lg:h-24 relative flex items-end justify-center pointer-events-none">
                <MontserratText
                  as="span"
                  sizes={{
                    laptop: "18px",
                    desktop: "20px",
                  }}
                  className="text-white/80 font-medium tracking-wide text-center max-w-[240px] sm:max-w-[260px] mx-auto leading-tight absolute bottom-0"
                >
                  {stat.label}
                </MontserratText>
              </div>

              {/* Statistic Value with CountUp (Below Label) */}
              <MontserratText
                as="span"
                sizes={{
                  mobile: "25px",
                  mobileLg: "25px",
                  tablet: "30px",
                  laptop: "35px",
                  desktop: "40px",
                }}
                className="text-[#DBB653] md:leading-[2.5rem] font-bold tracking-wider drop-shadow-sm mt-2 mb-2 lg:mt-3 lg:mb-3"
              >
                <CountUp value={stat.value} />
              </MontserratText>

              {/* Luxury Brochure Detail: Dot & Line (Hidden on mobile/tablet, shown on desktop) */}
              <div className="hidden lg:flex flex-col items-center w-full relative">
                {/* Dot sits at z-30 to stay in front of the gate overlap */}
                <div className="w-1.5 h-1.5 rounded-full bg-[#DBB653] shadow-[0_0_8px_#DBB653] z-30 relative" />
                {/* Decorative Line matching the dot - starts exactly at the center of the dot and goes down */}
                <div className="absolute top-[3px] left-1/2 -translate-x-1/2 w-[1px] h-48 md:h-80 bg-gradient-to-b from-[#DBB653] to-transparent opacity-35 z-20 pointer-events-none" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Showcase Image and Decorative Clouds (z-10 with negative margin to overlap Stats Grid) */}
      <div
        ref={cloudsRef}
        className="w-full relative -mt-10 sm:-mt-14 md:-mt-18 lg:-mt-24 z-10"
      >
        {/* Decorative Cloud Left */}
        <motion.div
          variants={leftCloudVariants}
          animate="float"
          className="absolute pointer-events-none z-0 select-none left-cloud"
        >
          <img
            ref={leftCloudRefImg}
            src={"/images/deco_Stats_left.svg"}
            alt=""
            loading="lazy"
            className="w-full h-auto opacity-100"
          />
        </motion.div>

        {/* Decorative Cloud Right */}
        <motion.div
          variants={rightCloudVariants}
          animate="float"
          className="absolute pointer-events-none z-0 select-none right-cloud"
        >
          <img
            ref={rightCloudRefImg}
            src={"/images/deco_Stats_right.svg"}
            alt=""
            loading="lazy"
            className="w-full h-auto opacity-100"
          />
        </motion.div>

        {/* The Gate Image */}
        <motion.div
          ref={gateRef}
          variants={gateVariants}
          initial="initial"
          animate={isGateInView && gateLoaded ? "animate" : "initial"}
          className="w-full overflow-hidden bg-transparent relative z-10"
          style={{ display: "block", fontSize: 0, lineHeight: 0 }}
        >
          <Image
            ref={gateRefImg}
            src={linkImage({
              src: gateImage || "/images/intruction.svg",
              size: "2k",
            })}
            onLoad={() => setGateLoaded(true)}
            alt="CCN Tay Bac Duc Hoa Dong Main Gate"
            width={1920}
            height={600}
            sizes="100vw"
            className="w-full h-auto object-cover object-center block"
            style={{
              imageRendering: "-webkit-optimize-contrast",
              filter: "brightness(1.04) contrast(1.2) saturate(1.4)",
            }}
          />
          {/* Fine framing border overlay — fully opaque at bottom to seal transition */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d3f4d] via-[#0d3f4d]/20 to-transparent pointer-events-none" />
        </motion.div>
      </div>
    </section>
  );
};
